import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import { FormikProps } from "formik";
import { CheckboxControl } from "formik-chakra-ui";
import { useEffect, useMemo, useState } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { Item } from "src/components/Inputs/SearchSelect/SearchSelect";
import {
  SCHOOL_CONFLICT_SUMMARY,
  useFieldError,
  useSchoolErrorById,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/context";
import { FormEngineRuleFormData } from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/schemas";
import {
  MultiSelectItem,
  School,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/FormEngineRuleDrawer/shared";
import * as GQL from "src/types/graphql";
import { GeographicPreferenceConfigurationForm } from "./GeographicPreferenceConfigurationForm";
import { getRuleType } from "./helpers";

export type QuestionRuleProps = {
  formik: FormikProps<FormEngineRuleFormData>;
  questions: GQL.GetFormEngineRuleQuestions_form_template_by_pk_sections_questions[];
  boundaries: { id: string; name: string | null }[];
  schools: School[];
  enableAddressQuestions: boolean;
};

export const QuestionRule: React.FC<QuestionRuleProps> = ({
  formik,
  questions,
  boundaries,
  schools,
  enableAddressQuestions,
}) => {
  const schoolErrorById = useSchoolErrorById();
  const answerOptionIdsError = useFieldError("answer_option_ids");
  const [answerOptions, setAnswerOptions] = useState<
    Map<string, MultiSelectItem[]>
  >(new Map());
  const questionTypeWhitelist = [
    GQL.question_type_enum.Address,
    GQL.question_type_enum.Grades,
    GQL.question_type_enum.SingleSelect,
    GQL.question_type_enum.MultiSelect,
  ].filter((q) =>
    q === GQL.question_type_enum.Address ? enableAddressQuestions : true
  );
  const selectItems: Item[] = questions
    .filter((q) => questionTypeWhitelist.includes(q.type))
    .map((q) => ({
      key: q.id,
      label: q.question,
    }));
  const questionTypeLookup = useMemo(
    () => new Map(questions.map((q) => [q.id, q.type])),
    [questions]
  );

  const { setFieldValue, values } = formik;

  useEffect(() => {
    if (values.question_id) {
      const questionType =
        questionTypeLookup.get(values.question_id) ?? undefined;
      setFieldValue("rule_type", getRuleType(questionType));
    } else {
      setFieldValue("rule_type", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionTypeLookup, values.question_id]);

  useEffect(() => {
    const getAnswerOptions = (
      questionId: string | undefined
    ): MultiSelectItem[] => {
      if (!questionId) return [];
      const selectedQuestion = questions.find((q) => q.id === questionId);

      if (!selectedQuestion) return [];
      switch (selectedQuestion.type) {
        case GQL.question_type_enum.Grades: {
          return (
            selectedQuestion.grades_question?.grades
              .sort(
                (a, b) =>
                  (a.grade_config?.order ?? 0) - (b.grade_config?.order ?? 0)
              )
              .map((g) => g.grade_config ?? { id: "", label: "" })
              .filter((g) => g.id && g.label) ?? []
          );
        }
        case GQL.question_type_enum.SingleSelect:
        case GQL.question_type_enum.MultiSelect: {
          return (
            selectedQuestion.form_question?.form_question_options ?? []
          ).sort((a, b) => a.order - b.order);
        }
        default:
          return [];
      }
    };
    const m: Map<string, MultiSelectItem[]> = new Map();
    for (const q of questions) {
      m.set(q.id, getAnswerOptions(q.id));
    }
    setAnswerOptions(m);
  }, [questions]);

  if (selectItems.length === 0) {
    return (
      <FormControl isInvalid>
        <FormErrorMessage m="0">
          No questions are configured for this form template. Add questions to
          the form template before using this option.
        </FormErrorMessage>
      </FormControl>
    );
  }

  return (
    <>
      <FormikSearchSelect
        name="question_id"
        label="Which question should this apply to?"
        placeholder="Select a question"
        items={selectItems}
      />
      {formik.values.rule_type ===
        GQL.form_engine_rule_type_enum.QUESTION_TAG && (
        <>
          <MultiSelectInput<MultiSelectItem>
            size="md"
            options={answerOptions.get(formik.values.question_id ?? "") ?? []}
            initialValues={(
              answerOptions.get(formik.values.question_id ?? "") ?? []
            ).filter((x) => formik.values.answer_option_ids.includes(x.id))}
            onChange={(selectedItems) => {
              formik.setFieldValue(
                "answer_option_ids",
                selectedItems.map((x) => x.id)
              );
            }}
            placeholder="Select one or multiple answers"
            validateSelectedItems={true}
            invalidSelectionErrorMessage="Select one or multiple answers"
            error={answerOptionIdsError}
            renderDropdownItem={(item) => (
              <Flex key={item.id}>{item.label}</Flex>
            )}
          />
          {/* start school filter section */}
          <FormControl>
            <FormLabel pb="0.325rem" m="0">
              Which schools should this rule apply to?
            </FormLabel>
            <MultiSelectInput
              size="md"
              options={schools.map((school) => ({
                id: school.id,
                label: school.name,
              }))}
              initialValues={formik.values.school_ids.map((schoolId) => {
                const school = schools.find((s) => s.id === schoolId);
                return {
                  id: schoolId,
                  label: school ? school.name : schoolId,
                };
              })}
              onChange={(selectedSchools) => {
                formik.setFieldValue(
                  "school_ids",
                  selectedSchools.map((s) => s.id)
                );
              }}
              placeholder="Select multiple"
              error={
                schoolErrorById.size > 0 ? SCHOOL_CONFLICT_SUMMARY : undefined
              }
              isInvalidItem={(item) => schoolErrorById.has(item.id)}
              getInvalidItemMessage={(item) => schoolErrorById.get(item.id)}
              renderDropdownItem={(item) => (
                <Flex key={item.id}>{item.label}</Flex>
              )}
            />
          </FormControl>
          <Text fontSize="sm" color="gray.600">
            The rule will apply only to the selected schools. If no schools are
            selected, it will apply to all schools.
          </Text>
          {/* end school filter section */}
        </>
      )}
      {formik.values.rule_type ===
        GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG && (
        <GeographicPreferenceConfigurationForm
          formik={formik}
          boundaries={boundaries}
          schools={schools}
        />
      )}
      <FormControl>
        <CheckboxControl
          name="enable_remove_tag"
          value={1}
          isChecked={formik.values.enable_remove_tag}
        >
          Remove tag when this condition is not met
        </CheckboxControl>
      </FormControl>
    </>
  );
};
